import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import GoogleAuthProvider from './contexts/GoogleAuthProvider';
import { ThemeProvider } from './contexts/ThemeContext';
import { PricingProvider } from './contexts/PricingContext';
import { TourOrderProvider } from './contexts/TourOrderContext';
import { HelmetProvider } from 'react-helmet-async';

// ─── Static Cache Layer Bootstrap ─────────────────────────────────────────────
// Kick off parallel fetches of all static JSON files from Vercel CDN
// IMMEDIATELY at module load — before any component mounts.
// By the time a user sees the homepage, tours/categories/images are already
// in memory and will be served in O(1) time with zero backend wait.
import { staticDataService } from './services/staticDataService';
staticDataService.prefetch().catch(() => {
  // Fail silently — live API fallback will handle it
});

// 🛡️ Global Security: Silence console logs in ALL environments IMMEDIATELY
// This prevent sensitive info leakage and simplifies the "Source of Truth"
// This will even silence logs on localhost as requested.
if (process.env.NODE_ENV === 'production' || window.location.hostname !== '') {
  const noop = () => {};
  console.log = noop;
  console.info = noop;
  console.warn = noop;
  console.debug = noop;
  console.group = noop;
  console.groupEnd = noop;
  console.groupCollapsed = noop;
}

// IMMEDIATE CACHE CLEAR - Force fresh data load
const CACHE_VERSION = 'v3_force_clear';

// Add Spy for Debugging
const originalRemoveItem = localStorage.removeItem;
export const originalSetItem = localStorage.setItem;
const originalClear = localStorage.clear;

localStorage.removeItem = function (key: string) {
  if (key === 'authToken') {
    console.group('🚨 AuthToken REMOVED via removeItem!');
    console.warn('Trace below:');
    console.trace();
    console.groupEnd();
  }
  originalRemoveItem.call(localStorage, key);
};

localStorage.setItem = function (key: string, value: string) {
  if (key === 'authToken' && (!value || value === 'null' || value === 'undefined')) {
    console.group('🚨 AuthToken OVERWRITTEN with null/empty!');
    console.warn(`Value being set: "${value}"`);
    console.warn('Trace below:');
    console.trace();
    console.groupEnd();
  }
  originalSetItem.call(localStorage, key, value);
};

localStorage.clear = function () {
  console.group('🚨 LocalStorage CLEARED via clear()!');
  console.warn('This wipes everything, including authToken. Trace below:');
  console.trace();
  console.groupEnd();
  originalClear.call(localStorage);
};

if (localStorage.getItem('app_cache_version') !== CACHE_VERSION) {
  localStorage.removeItem('category_images_cache');
  localStorage.removeItem('category_images_cache_timestamp');
  localStorage.removeItem('categories_cache_backend');
  localStorage.removeItem('categories_cache_backend_timestamp');
  localStorage.removeItem('tours_cache_backend');
  localStorage.removeItem('tours_cache_backend_timestamp');
  localStorage.removeItem('tours_cache_backend_url');
  localStorage.removeItem('images_cache_backend');
  localStorage.removeItem('images_cache_backend_timestamp');
  localStorage.setItem('app_cache_version', CACHE_VERSION);
}
// Suppress harmless WebSocket errors from dev tools/extensions
const originalConsoleError = console.error;
console.error = (...args: any[]) => {
  if (args[0] && typeof args[0] === 'string' && args[0].includes('WebSocket connection to')) {
    return;
  }
  originalConsoleError.apply(console, args);
};

// 🚀 Aggressive Cache Busting for Production
if ('caches' in window) {
  caches.keys().then((names) => {
    for (let name of names) {
      caches.delete(name);
    }
  });
}

// 🚀 Service Worker Unregistration to fix stuck caches
serviceWorkerRegistration.unregister();

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '309690203315-8ess3ccb1r6jj52u9v1ehpdka48gv1ak.apps.googleusercontent.com';

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <GoogleAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <ThemeProvider>
          <App />
        </ThemeProvider>
      </GoogleAuthProvider>
    </HelmetProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
